import React, { useMemo, useState } from "react";
import { Button, Checkbox, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnergyPerformanceInstallationform } from "../../../../api";
import { P, avisarSoloLectura, motor, useEnergia, useGuardarSeccion, useSoloLectura } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { EditableTable } from "../common/EditableTable";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";
import "./BaselineScreen.scss";

const energyPerformanceCtrl = new EnergyPerformanceInstallationform();
const TIPOS_ENERGIA = ["energia_red_kwh", "autogeneracion_kwh", "energia_termica_kwh"];

const tomar = (inst) => ({
  consumos_mensuales: (inst.consumos_mensuales || []).map((m) => ({ ...m, excluir: m.excluir === true })),
  var2Nombre: inst.var2Nombre || "",
  var2Unidad: inst.var2Unidad || "",
  factoresEstaticos: inst.factoresEstaticos || [],
});
const fmt = (v, d = 0) => P.fmt(v, d);
const pTexto = (p) => (p == null || !isFinite(p) ? "—" : p < 0.001 ? "<0,001" : fmt(p, 3));

// Clase del rótulo de la LBEn, como el prototipo: utilizable y sin reparos = ok; baja precisión o media con pendiente negativa = aviso
function claseLbEn(r) {
  if (!P.modeloApto(r)) return "Brecha mayor";
  if (r.tipo === "regresion" || r.tipo === "var2" || (r.tipo === "media" && !/negativa/.test(r.motivoMedia || ""))) return "Cumple";
  return "Brecha parcial";
}

// Registro de una versión nueva de la LBEn (período o modelo): se valida con el motor y se guarda en el servidor.
function useVersionLbEn(installation, onSaved, t) {
  const { accessToken } = useAuth();
  const soloLectura = useSoloLectura();
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const registrar = async (validar, llamada) => {
    setMensaje(null);
    if (soloLectura) { avisarSoloLectura(t, soloLectura); return false; }
    try {
      validar();
    } catch (e) {
      setMensaje({ tipo: "danger", texto: e.message });
      return false;
    }
    setEnviando(true);
    try {
      const res = await llamada(accessToken, installation._id);
      if (res.warning) setMensaje({ tipo: "warn", texto: res.warning });
      toast.success(t("energy_performance_baseline_version_saved"));
      onSaved?.(res.installation);
      return true;
    } catch (error) {
      setMensaje({ tipo: "danger", texto: error?.message || t("server_connection_error") });
      return false;
    } finally {
      setEnviando(false);
    }
  };
  return { registrar, enviando, mensaje };
}

function PeriodoBase({ f, installation, onSaved, bloqueado, t }) {
  const sp = P.lbSplit(f);
  const r = f._reg;
  const meses = (f._monthly || []).map((m) => m.month);
  const ini = sp.base.length ? sp.base[0].month : "";
  const fin = sp.base.length ? sp.base[sp.base.length - 1].month : "";
  const [desde, setDesde] = useState(ini);
  const [hasta, setHasta] = useState(fin);
  const [motivo, setMotivo] = useState("");
  const { registrar, enviando, mensaje } = useVersionLbEn(installation, onSaved, t);
  const reales = meses.length > 0 && meses.every((m) => /^\d{4}-\d{2}$/.test(m));
  const version = (f.lbHist || []).length + 1;

  const enviar = async () => {
    const ok = await registrar(
      () => motor.lineaBase.nuevaVersionPeriodo(f, { desde, hasta, motivo }),
      (tok, id) => energyPerformanceCtrl.registerBaselinePeriod(tok, id, { from: desde, to: hasta, reason: motivo }),
    );
    if (ok) setMotivo("");
  };

  return (
    <Section title={t("energy_performance_baseline_period_title")} note={t("energy_performance_baseline_period_note")}>
      <div className="ep-table">
        <table>
          <tbody>
            <tr>
              <td><strong>{t("energy_performance_baseline_version").replace("{n}", version)}</strong></td>
              <td className="ep-mono">
                {ini || "—"} {t("energy_performance_to")} {fin || "—"} · {r.nBase != null ? r.nBase : sp.base.length} {t("energy_performance_months")}
                {sp.excl.length > 0 && ` (${t("energy_performance_plus_excluded").replace("{n}", sp.excl.length)})`}
              </td>
            </tr>
            <tr>
              <td><strong>{t("energy_performance_report_period")}</strong></td>
              <td className="ep-mono">
                {sp.rep.length ? `${sp.rep[0].month} ${t("energy_performance_to")} ${sp.rep[sp.rep.length - 1].month} (${sp.rep.length} ${t("energy_performance_months")})`
                  : t("energy_performance_report_period_none").replace("{rep}", P.N_REP_MIN).replace("{base}", P.N_MIN)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {reales ? (
        <div className="ep-baseline__version-form">
          <div>
            <label htmlFor="lb-desde">{t("energy_performance_from")}</label>
            <select id="lb-desde" value={desde} onChange={(e) => setDesde(e.target.value)}>{meses.map((m) => <option key={m}>{m}</option>)}</select>
          </div>
          <div>
            <label htmlFor="lb-hasta">{t("energy_performance_to_label")}</label>
            <select id="lb-hasta" value={hasta} onChange={(e) => setHasta(e.target.value)}>{meses.map((m) => <option key={m}>{m}</option>)}</select>
          </div>
          <div className="ep-baseline__reason">
            <label htmlFor="lb-motivo">{t("energy_performance_change_reason")}</label>
            <input id="lb-motivo" type="text" value={motivo} placeholder={t("energy_performance_change_reason_placeholder")} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <Button type="button" primary size="small" loading={enviando} disabled={enviando || bloqueado} onClick={enviar}>
            {t("energy_performance_register_version")}
          </Button>
        </div>
      ) : (
        <p className="ep-section__note">{t("energy_performance_months_without_year")}</p>
      )}
      {bloqueado && <p className="ep-section__note">{t("energy_performance_save_before_version")}</p>}
      {mensaje && <Notice tipo={mensaje.tipo}>{mensaje.texto}</Notice>}
      {(f.lbHist || []).length > 0 && (
        <div className="ep-table ep-baseline__history">
          <table>
            <thead>
              <tr>
                <th>{t("energy_performance_version")}</th><th>{t("energy_performance_date")}</th><th>{t("energy_performance_change")}</th>
                <th>{t("energy_performance_before")}</th><th>{t("energy_performance_after")}</th><th>{t("energy_performance_reason")}</th><th>{t("energy_performance_user")}</th>
              </tr>
            </thead>
            <tbody>
              {f.lbHist.map((x, i) => (
                <tr key={i}>
                  <td>{x.version || i + 2}</td>
                  <td className="ep-mono">{String(x.fecha || "").slice(0, 10)}</td>
                  <td>{x.tipo === "modelo" ? t("energy_performance_model") : t("energy_performance_baseline_period")}</td>
                  <td className="ep-mono">{x.anterior || ""}</td>
                  <td className="ep-mono">{x.tipo === "modelo" ? x.modelo : `${x.desde} ${t("energy_performance_to")} ${x.hasta}`}</td>
                  <td>{x.motivo}</td>
                  <td>{x.usuario || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Variable2({ f, borrador, cambiar, installation, onSaved, bloqueado, t }) {
  const ev = motor.lineaBase.evaluarVar2(f);
  const [motivo, setMotivo] = useState("");
  const { registrar, enviando, mensaje } = useVersionLbEn(installation, onSaved, t);
  const nombre = borrador.var2Nombre || t("energy_performance_var2_default");
  const adoptada = f.lbModelo === "var2";
  const accion = async (adoptar) => {
    const ok = await registrar(
      () => motor.lineaBase.nuevaVersionModelo(f, { adoptar, motivo }),
      (tok, id) => energyPerformanceCtrl.registerBaselineModel(tok, id, { adopt: adoptar, reason: motivo }),
    );
    if (ok) setMotivo("");
  };
  return (
    <Section title={t("energy_performance_var2_title")} note={t("energy_performance_var2_note")}>
      <div className="ep-grid">
        <div>
          <label htmlFor="v2-nombre">{t("energy_performance_var2_name")}</label>
          <input id="v2-nombre" type="text" value={borrador.var2Nombre} onChange={(e) => cambiar("var2Nombre", e.target.value)} />
        </div>
        <div>
          <label htmlFor="v2-unidad">{t("energy_performance_unit")}</label>
          <input id="v2-unidad" type="text" value={borrador.var2Unidad} onChange={(e) => cambiar("var2Unidad", e.target.value)} />
        </div>
      </div>
      {!ev.r1 && <p className="ep-section__note">{ev.motivo === "Sin datos de la variable 2 en todos los meses del período base." ? t("energy_performance_var2_no_data") : ev.motivo}</p>}
      {ev.r1 && ev.r2 && (
        <div className="ep-table">
          <table>
            <thead>
              <tr><th>{t("energy_performance_model")}</th><th>R²</th><th>CV(RMSE)</th><th>{t("energy_performance_p_production")}</th><th>p {nombre}</th></tr>
            </thead>
            <tbody>
              <tr><td>{t("energy_performance_production_only")}</td><td className="ep-mono">{fmt(ev.r1.r2 * 100, 1)}%</td><td className="ep-mono">{fmt(ev.r1.cvrmse, 1)}%</td><td className="ep-mono">{pTexto(ev.r1.pA)}</td><td>—</td></tr>
              <tr><td>{t("energy_performance_production_plus")} {nombre}</td><td className="ep-mono">{fmt(ev.r2.r2 * 100, 1)}%</td><td className="ep-mono">{fmt(ev.r2.cvrmse, 1)}%</td><td className="ep-mono">{pTexto(ev.r2.p[1])}</td><td className="ep-mono">{pTexto(ev.r2.p[2])}</td></tr>
            </tbody>
          </table>
        </div>
      )}
      {ev.r2 && (
        <p className="ep-section__note">
          {ev.disponible ? <StatusBadge estado="Cumple">{t("energy_performance_var2_significant")}</StatusBadge> : <StatusBadge estado="Pendiente">{t("energy_performance_var2_not_significant")}</StatusBadge>}{" "}
          {ev.disponible && t("energy_performance_var2_should_consider")}
        </p>
      )}
      {f._avisoVar2 && <Notice titulo={t("energy_performance_notice")}>{t("energy_performance_var2_warning")}</Notice>}
      {(adoptada || ev.disponible) && (
        <div className="ep-baseline__version-form">
          <div className="ep-baseline__reason">
            <label htmlFor="v2-motivo">{adoptada ? t("energy_performance_var2_revert_reason") : t("energy_performance_var2_adopt_reason")}</label>
            <input id="v2-motivo" type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          {adoptada ? (
            <Button type="button" basic size="small" loading={enviando} disabled={enviando || bloqueado} onClick={() => accion(false)}>{t("energy_performance_var2_revert")}</Button>
          ) : (
            <Button type="button" primary size="small" loading={enviando} disabled={enviando || bloqueado} onClick={() => accion(true)}>{t("energy_performance_var2_adopt")}</Button>
          )}
        </div>
      )}
      {adoptada && <p><StatusBadge estado="Cumple">{t("energy_performance_var2_adopted")}</StatusBadge></p>}
      {mensaje && <Notice tipo={mensaje.tipo}>{mensaje.texto}</Notice>}
    </Section>
  );
}

function AhorroNormalizado({ f, t }) {
  const r = f._reg;
  const a = P.ahorroNormalizado(f);
  let pendiente = null;
  if (!P.modeloApto(r)) pendiente = P.modeloMotivo(r);
  else if (!r.reporte) pendiente = t("energy_performance_savings_no_report").replace("{n}", P.N_REP_MIN);
  else if (a && a.faltaVar2) pendiente = t("energy_performance_savings_missing_var2");
  else if (!a) pendiente = t("energy_performance_savings_not_computable");
  return (
    <Section title={t("energy_performance_savings_title")} note={t("energy_performance_savings_note")}>
      {pendiente ? (
        <p><StatusBadge estado="Pendiente" /> {pendiente}</p>
      ) : (
        <>
          <div className="ep-kpis">
            <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_expected")}</div><div className="ep-kpi__value">{fmt(a.esperado)}</div><div className="ep-kpi__sub">kWh · {a.k} {t("energy_performance_months")}</div></div>
            <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_actual")}</div><div className="ep-kpi__value">{fmt(a.real)}</div><div className="ep-kpi__sub">kWh</div></div>
            <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_savings")}</div><div className="ep-kpi__value">{fmt(a.ahorro)}</div><div className="ep-kpi__sub">kWh · {fmt(a.pct, 1)}%</div></div>
            <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ci90")}</div><div className="ep-kpi__value ep-baseline__ci">{fmt(a.ic[0])} … {fmt(a.ic[1])}</div><div className="ep-kpi__sub">ρ {fmt(a.rho, 2)} · gl {a.gl}</div></div>
          </div>
          <p>
            {a.significativo ? <StatusBadge estado="Cumple">{t("energy_performance_savings_significant")}</StatusBadge> : <StatusBadge estado="Mejorable">{t("energy_performance_savings_within_error")}</StatusBadge>}
          </p>
          {a.fueraRango && <Notice>{t("energy_performance_savings_extrapolation")}</Notice>}
        </>
      )}
    </Section>
  );
}

// Línea de base energética (LBEn) — cláusula 6.5 (hoja 01, bloques 4 y 13; filas 21, 27–31, 80–83, 118, 137, 139).
export function BaselineScreen({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { archivo, inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar, marcarInvalidos, invalidos, version } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const [porTipo, setPorTipo] = useState(false);

  // vista en vivo con lo que se está editando (como el prototipo: se recalculan la recta, el R², el CV y los desvíos)
  const vivoRes = useMemo(() => {
    if (!archivo || !b) return null;
    return motor.calcularVista({ ...archivo, instalacion: { ...inst, ...b } });
  }, [archivo, inst, b]);
  const vivo = vivoRes && vivoRes.f ? vivoRes : null;
  const errorVivo = dirty && vivoRes && vivoRes.error;
  const fv = (vivo && vivo.f) || f;

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;
  if (!fv) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const r = fv._reg;
  const tieneTipos = porTipo || b.consumos_mensuales.some((m) => TIPOS_ENERGIA.some((k) => m[k] != null));
  // esperado y desvío por mes (el motor ordena los meses: se alinea por mes)
  const porMes = {};
  (fv._monthly || []).forEach((m, i) => {
    const exp = r.predicted[i];
    const usable = P.modeloApto(r) && exp > 0;
    porMes[m.month] = { exp, dev: usable ? ((m.energy - exp) / exp) * 100 : null };
  });
  const umbral = P.umbralDesvio(r);
  const unidad = fv.unidadProdCorta || "u";
  const camb = P.factoresEstaticosCambiados(fv);
  const lbTipos = P.lbPorTipo(fv);

  const columnas = [
    { key: "month", label: t("energy_performance_month"), width: 90 },
    { key: "production", label: `${t("energy_performance_production")} (${unidad})`, type: "num" },
    { key: "energy", label: t("energy_performance_energy_kwh"), type: "num" },
    ...(tieneTipos ? [
      { key: "energia_red_kwh", label: t("energy_performance_grid_kwh"), type: "num" },
      { key: "autogeneracion_kwh", label: t("energy_performance_selfgen_kwh"), type: "num" },
      { key: "energia_termica_kwh", label: t("energy_performance_thermal_kwh"), type: "num" },
    ] : []),
    { key: "var2", label: b.var2Nombre || t("energy_performance_var2_default"), type: "num" },
    { key: "_exp", label: t("energy_performance_expected_lben"), type: "view", render: (m) => <span className="ep-mono">{porMes[m.month] ? fmt(porMes[m.month].exp) : "—"}</span> },
    {
      key: "_dev", label: t("energy_performance_deviation"), type: "view", render: (m) => {
        const d = porMes[m.month] && porMes[m.month].dev;
        if (d == null) return "—";
        return <span className={`ep-mono ${Math.abs(d) > umbral ? "ep-baseline__dev--bad" : "ep-baseline__dev--ok"}`}>{d > 0 ? "+" : ""}{fmt(d, 1)}%</span>;
      },
    },
    { key: "evento", label: t("energy_performance_event") },
    { key: "excluir", label: t("energy_performance_exclude"), type: "check" },
  ];

  const onSave = () => {
    const meses = b.consumos_mensuales.map((m) => {
      const x = { ...m };
      TIPOS_ENERGIA.forEach((k) => { if (x[k] == null) delete x[k]; });
      if (x.var2 == null) delete x.var2;
      return x;
    });
    guardar({ ...b, consumos_mensuales: meses });
  };

  return (
    <div className="ep-screen ep-baseline">
      <h3 className="ep-screen__title">{t("energy_performance_baseline_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_baseline_intro")}</p>

      <div className="ep-kpis">
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_slope")} — kWh/{unidad}</div><div className="ep-kpi__value">{fmt(r.a, 3)}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_intercept")} — kWh</div><div className="ep-kpi__value">{fmt(r.b, 0)}</div></div>
        <div className="ep-kpi">
          <div className="ep-kpi__label">R²</div><div className="ep-kpi__value">{fmt(r.r2 * 100, 1)}%</div>
          <div className="ep-kpi__sub">{r.pA != null && isFinite(r.pA) ? `p = ${pTexto(r.pA)} ${r.pA < P.P_MAX ? t("energy_performance_significant") : t("energy_performance_not_significant")}` : ""}</div>
        </div>
        <div className="ep-kpi"><div className="ep-kpi__label">CV(RMSE) · NMBE</div><div className="ep-kpi__value">{fmt(r.cvrmse, 1)}% · {fmt(r.nmbe, 1)}%</div><div className="ep-kpi__sub">{t("energy_performance_cv_criterion")}</div></div>
      </div>
      <p className="ep-baseline__type"><StatusBadge estado={claseLbEn(r)}>{P.lbEtiqueta(r)}</StatusBadge> <span className="ep-section__note">{P.lbDescripcion(r)}</span></p>
      {fv._avisoMeses && <Notice>{fv._avisoMeses}</Notice>}
      {vivo && dirty && <Notice tipo="info">{t("energy_performance_live_preview")}</Notice>}
      {errorVivo && <Notice tipo="danger" titulo={t("energy_performance_live_invalid")}>{errorVivo}</Notice>}

      <div className="ep-charts"><GraficoCatalogo f={fv} id="real_vs_lben" t={t} /><GraficoCatalogo f={fv} id="dispersion" t={t} /></div>
      {f && <PeriodoBase key={`pb-${installation.updatedAt}`} f={f} installation={installation} onSaved={onSaved} bloqueado={dirty} t={t} />}

      <AhorroNormalizado f={fv} t={t} />

      <Section title={t("energy_performance_monthly_title")} note={t("energy_performance_monthly_note")}
        actions={<Checkbox toggle label={t("energy_performance_show_energy_types")} checked={tieneTipos} onChange={(_, d) => setPorTipo(d.checked)} />}>
        <EditableTable
          key={`meses-${version}-${tieneTipos}`}
          t={t}
          rows={b.consumos_mensuales}
          columns={columnas}
          onChange={(v) => cambiar("consumos_mensuales", v)}
          onErrors={(n) => marcarInvalidos("meses", n)}
          newRow={() => ({ month: "", production: null, energy: null, evento: "", excluir: false })}
          addLabel={t("energy_performance_add_month")}
          emptyText={t("energy_performance_no_months")}
        />
      </Section>

      <Section title={t("energy_performance_lben_by_type_title")} note={t("energy_performance_lben_by_type_note")}>
        {lbTipos.length ? (
          <div className="ep-table">
            <table>
              <thead><tr><th>{t("energy_performance_energy_type")}</th><th>{t("energy_performance_base_energy")}</th><th>{t("energy_performance_lben_type")}</th><th>{t("energy_performance_detail")}</th></tr></thead>
              <tbody>{lbTipos.map((x) => (
                <tr key={x.k}><td>{x.lbl}</td><td className="ep-mono">{fmt(x.total)}</td><td>{P.lbEtiqueta(x.r)}</td><td>{P.lbDescripcion(x.r)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="ep-section__note">{t("energy_performance_lben_by_type_none")}</p>}
      </Section>

      <Section title={t("energy_performance_static_factors_title")} note={t("energy_performance_static_factors_note")}>
        <EditableTable
          key={`fe-${version}`}
          t={t}
          rows={b.factoresEstaticos}
          onChange={(v) => cambiar("factoresEstaticos", v)}
          newRow={() => P.ROW_TEMPLATES.factoresEstaticos()}
          addLabel={t("energy_performance_add_static_factor")}
          emptyText={t("energy_performance_static_factors_empty")}
          columns={[
            { key: "factor", label: t("energy_performance_static_factor") },
            { key: "valor_lb", label: t("energy_performance_value_in_lben") },
            { key: "valor_actual", label: t("energy_performance_current_value") },
            { key: "fecha", label: t("energy_performance_change_date") },
          ]}
        />
        {camb.length > 0 && (
          <Notice titulo={t("energy_performance_review_lben")}>
            {t("energy_performance_static_changed")}: {camb.map((x) => `${x.factor} (${x.valor_lb} → ${x.valor_actual})`).join("; ")}.
          </Notice>
        )}
      </Section>

      <Variable2 key={`v2-${installation.updatedAt}`} f={fv} borrador={b} cambiar={cambiar} installation={installation} onSaved={onSaved} bloqueado={dirty} t={t} />

      <Section title={t("energy_performance_scale_down_title")} note={t("energy_performance_scale_down_note")} />

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={invalidos} saving={saving} onSave={onSave} onDiscard={descartar} t={t} />
    </div>
  );
}
